<template>
  <div
    class="layer"
    :class="layerClassName"
    :title="title"
    data-entity="producer-editor-selectLayer"
    :data-test="`graphics-editor-layer-${element.id}`"
    @click="$emit('select', element)"
  >
    <template v-if="isGroup">
      <button
        class="tb-btn tb-btn-link sm low-em group-arrow"
        data-entity="producer-editor-toggleGroupCollapse"
        :data-test="`graphics-editor-layer-collapseToggler-${element.id}`"
        @click.stop="toggleField('collapsed')"
      >
        <span
          class="fas"
          :class="getGroupArrowClass"
        />
      </button>
    </template>

    <span :class="iconClassName" />

    <div class="layer-name ml-1" :data-test="`graphics-editor-layer-name-${element.id}`">
      {{ element.name }}
    </div>

    <div class="layer-actions ml-1">
      <!-- visibility of social layers is handled programmatically,
        so we don't allow users to hide/show manually -->
      <button
        v-if="!toggleVisibleDisabled"
        class="tb-btn tb-btn-link sm low-em layer-visibility"
        data-entity="producer-editor-toggleLayerVisibility"
        :data-test="`graphics-editor-layer-visibility-${element.id}`"
        @click.stop="toggleVisible"
      >
        <span class="fas" :class="[visible ? 'fa-eye' : 'fa-eye-slash']" />
      </button>

      <button
        v-if="element.type === 'slideshow'"
        class="tb-btn tb-btn-link sm low-em add-slide"
        data-entity="producer-editor-addSlideLayer"
        :data-test="`graphics-editor-addSlideLayer-${element.id}`"
        @click.stop="$emit('add-slide', element)"
      >
        <span class="fas fa-plus" />
      </button>
    </div>
  </div>
</template>

<script>
import { capitalize } from 'lodash-es';

const typeLabel = (type) => (
  type
    .replace(/-/g, ' ')
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ')
);

export default {
  props: {
    element: {
      required: true,
    },

    selected: {
      required: true,
    },

    graphic_field: {
      required: true,
    },
  },

  computed: {
    getGroupArrowClass() {
      return this.element.collapsed ? 'fa-caret-right' : 'fa-caret-down';
    },

    visible() {
      return this.graphic_field?.visible ?? true;
    },

    title() {
      return `${this.element.name} - ${typeLabel(this.element.type)} Element`;
    },

    isGroup() {
      return [
        'group',
        'slideshow',
        'data-list',
        'sheet-data',
        'social-group',
        'social-data',
        'sportradar',
        'shoppable',
      ].includes(this.element.type);
    },

    layerClassName() {
      const { element, selected } = this;

      return {
        editable: element.editable,
        selected: selected && selected.id === element.id,
        group: this.isGroup,
        slideshow: element.type === 'slideshow',
        noEditElement: !element.editable,
      };
    },

    iconClassName() {
      const { collapsed, type } = this.element;

      const layerClassNames = {
        timer: 'fas fa-stopwatch',
        slideshow: 'fas fa-presentation',
        group: `fas fa-folder${collapsed ? '' : '-open'}`,
        'sheet-data': 'fas fa-table',
        'data-list': 'fas fa-list',
        'social-group': '',
        'social-data': 'far fa-phone-laptop',
        'social-display': 'far fa-phone-laptop',
        'poll-display': 'far fa-poll-people',
        rectangle: 'fas fa-square',
        oval: 'fas fa-circle',
        triangle: 'fas fa-triangle',
        date: 'fas fa-clock',
        text: 'fas fa-text',
        qrcode: 'fas fa-qrcode',
        image: 'fas fa-image',
        video: 'fas fa-video',
        sportradar: 'fas fa-radar',
        'marquee-image': 'fas fa-image',
        'marquee-text': 'fas fa-text',
        marquee: 'fak fa-input-text-regular',
        shoppable: 'fas fa-cart-plus',
      };

      return layerClassNames[type] || `icon icon-${type}`;
    },

    toggleVisibleDisabled() {
        return this.element.data?.required;
    },
  },

  methods: {
    toggleField(field) {
      this.$set(this.element, field, !this.element[field]);
    },

    toggleVisible() {
      this.$emit('toggleVisible');
    },
  },

  created() {
    if (this.element.type === 'social-group') {
      this.toggleField('collapsed');
    }
  },
};
</script>
